import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateShift } from '../../../redux/shifts/shiftsThunks';

/**
 * Hook for handling shift updates with automatic cost recalculation
 *
 * This hook manages the process of updating shift data in the store. When time-related
 * fields (start/end) are updated, it automatically recalculates the projected cost
 * using the provided calculateShiftCost function.
 *
 * @param {Object} shift - The current shift object
 * @param {Function} calculateShiftCost - Cost calculation function from useShiftCostCalculation
 * @returns {Function} Update function that takes partial shift fields and dispatches the update
 *
 * @example
 * const calculateShiftCost = useShiftCostCalculation(shiftTypes);
 * const handleShiftUpdate = useShiftUpdater(shift, calculateShiftCost);
 *
 * // Update shift times with automatic cost recalculation
 * handleShiftUpdate({ start: newStart, end: newEnd });
 *
 * // Update other fields without affecting cost
 * handleShiftUpdate({ notes: "New note" });
 */
export const useShiftUpdater = (shift, calculateShiftCost) => {
  const dispatch = useDispatch();

  return useCallback(
    (fields) => {
      if (!shift?.id) return;

      const updatedFields = { ...fields };

      if (fields.start || fields.end || fields.name) {
        const projectedCost = calculateShiftCost(
          fields.start || shift.start,
          fields.end || shift.end,
          fields.name || shift.name
        );
        if (projectedCost) {
          updatedFields.projectedCost = projectedCost;
        }
      }

      dispatch(updateShift({ shiftId: shift.id, updatedFields }));
    },
    [dispatch, shift, calculateShiftCost]
  );
};
